<template>
  <div class="date-input-dropdown">
    <div class="date-input-dropdown__header">
      <span>{{ title }}</span>
      <Icon icon="cross" @click="close" />
    </div>

    <div class="date-input-dropdown__navigator">
      <Icon icon="v" @click="prev" :class="{
        'date-input-dropdown__navigator-arrow': true,
        'date-input-dropdown__navigator-arrow--left': true,
        'date-input-dropdown__navigator-arrow--inactive': !isPrevActive,
        }" />

      <span v-if="currentMode == 'year'" class="date-input-dropdown__navigator-selected date-input-dropdown__navigator-selected--inactive">
        {{ years[0] }} - {{ years[11] }}
      </span>
      <span v-if="currentMode == 'month'" class="date-input-dropdown__navigator-selected" @click="currentMode = 'year'">
        {{ date.format('YYYY') }}
      </span>
      <span v-if="currentMode == 'day'" class="date-input-dropdown__navigator-selected" @click="currentMode = 'month'">
        {{ date.format('MMMM YYYY') }}
      </span>

      <Icon icon="v" @click="next" :class="{
        'date-input-dropdown__navigator-arrow': true,
        'date-input-dropdown__navigator-arrow--right': true,
        'date-input-dropdown__navigator-arrow--inactive': !isNextActive,
        }" />
    </div>

    <div v-if="currentMode == 'year'" class="date-input-dropdown__year-selector">
      <div v-for="year in years" :key="year" @click="selectYear(year)"
        :class="{
          'date-input-dropdown__year': true,
          'date-input-dropdown__year--inactive': isYearEnabled(year),
        }">
        {{ year }}
      </div>
    </div>

    <div v-if="currentMode == 'month'" class="date-input-dropdown__month-selector">
      <div v-for="month in months" :key="month" @click="selectMonth(month)"
        :class="{
          'date-input-dropdown__month': true,
          'date-input-dropdown__month--inactive': isMonthEnabled(month),
        }">
        {{ month }}
      </div>
    </div>

    <div v-if="currentMode == 'day'" class="date-input-dropdown__day-selector">
      <WeekDays class="date-input-dropdown__weekdays" />
      <Month class="date-input-dropdown__month" :date="date" :valid="validDates" @change="selectDay" />
    </div>
  </div>
</template>

<script>
import moment, { Moment } from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import AutoCloseMixin from '@/components/AutoCloseMixin'
import Month from '@/components/calendar/Month'
import WeekDays from '@/components/calendar/WeekDays'
import Icon from '@/components/Icon'

@Component({
  mixins: [
    AutoCloseMixin,
  ],
  components: {
    Icon,
    WeekDays,
    Month,
  },
  props: {
    value: {
      type: Moment,
      required: true,
    },
    min: {
      type: Moment,
      required: false,
      default: () => moment(0).subtract(3, 'years').startOf('day'),
    },
    max: {
      type: Moment,
      required: false,
      default: () => moment().startOf('day'),
    },
    mode: {
      type: String,
      required: false,
      default: 'year', // 'year', 'month', 'day'
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
  },
})
export default class DateInputDropdown extends Vue {
  currentMode = String(this.mode)
  date = this.value.isZero() ? moment(this.max).startOf('day') : moment(this.value).startOf('day')
  year = moment(this.date).year()

  get validDates () {
    return moment.range(this.min, this.max)
  }

  get years () {
    const result = []
    for (let i = this.year - 8; i < this.year + 4; i++) {
      result.push(i)
    }
    return result
  }

  get months () {
    return moment.localeData().months().clone()
  }

  get isPrevActive () {
    if (this.currentMode === 'year' && this.year - 4 > this.min.year()) {
      return true
    } else if (this.currentMode === 'month' && moment(this.date).startOf('year').isAfter(this.min)) {
      return true
    } else if (this.currentMode === 'day' && moment(this.date).startOf('month').isAfter(this.min)) {
      return true
    } else {
      return false
    }
  }

  get isNextActive () {
    if (this.currentMode === 'year' && this.year + 8 <= this.max.year()) {
      return true
    } else if (this.currentMode === 'month' && moment(this.date).endOf('year').isBefore(this.max)) {
      return true
    } else if (this.currentMode === 'day' && moment(this.date).endOf('month').isBefore(this.max)) {
      return true
    } else {
      return false
    }
  }

  prev () {
    if (this.currentMode === 'year') {
      this.year -= 12
    } else if (this.currentMode === 'month') {
      this.date = moment(this.date).subtract(1, 'year')
      this.year = this.date.year()
    } else if (this.currentMode === 'day') {
      this.date = moment(this.date).subtract(1, 'month')
      this.year = this.date.year()
    }
  }

  next () {
    if (this.currentMode === 'year') {
      this.year += 12
    } else if (this.currentMode === 'month') {
      this.date = moment(this.date).add(1, 'year')
      this.year = this.date.year()
    } else if (this.currentMode === 'day') {
      this.date = moment(this.date).add(1, 'month')
      this.year = this.date.year()
    }
  }

  isYearEnabled (year) {
    return year > this.max.year() || year < this.min.year()
  }

  selectYear (year) {
    this.year = year
    this.date = moment(this.date).year(year)
    this.currentMode = 'month'
  }

  isMonthEnabled (month) {
    return moment(this.date).month(month).isAfter(this.max) || moment(this.date).month(month).isBefore(this.min)
  }

  selectMonth (month) {
    this.date = moment(this.date).month(month)
    this.currentMode = 'day'
  }

  selectDay (day) {
    this.date = moment(day)
    this.currentMode = 'year'
    this.$emit('input', moment(day).startOf('day'))
  }
}
</script>

<style lang="scss">
@import './dialog';

.date-input-dropdown {
  position: absolute;
  width: 100%;
  height: fit-content;
  background-color: white;
  cursor: default;
  @include dialog();

  &__header {
    padding: 8px 11px 12px 20px;
    text-transform: uppercase;
    font-size: 11px;
    font-family: 'Gotham';
    font-weight: bold;
    color: #005CA9;
    display: flex;
    justify-content: center;
    align-items: center;

    span:first-child {
      flex: 1 0 auto;
      text-align: center;
    }

    .figure {
      cursor: pointer;
    }
  }

  &__navigator {
    display: grid;
    grid-template-columns: auto 1fr auto;
    color: #005CA9;
    font-family: 'Gotham';
    font-weight: bold;
    font-size: 12px;
    padding: 3px 20px;
    text-transform: uppercase;

    .figure {
      font-size: 7px;
      height: 10px;
      margin-top: 4px;
    }
  }

  &__navigator-selected {
    text-align: center;

    &:hover {
      font-weight: bold;
      cursor: pointer;
    }

    &--inactive {
      pointer-events: none;
    }
  }

  &__navigator-arrow {
    &:hover {
      font-weight: bold;
      cursor: pointer;
    }

    &--inactive {
      opacity: .6;
      pointer-events: none;
    }

    &--left {
      transform: rotate(90deg);

      &::before {
        margin-right: 1px;
      }
    }

    &--right {
      transform: rotate(-90deg);

      &::before {
        margin-left: 1px;
      }
    }
  }

  &__year-selector {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 7px;
    font-size: 12px;
    font-family: 'Gotham';
    font-weight: bold;
    color: #787878;

    > div {
      border-top: 1px solid rgba(0,0,0,.1);
      border-right: 1px solid rgba(0,0,0,.1);
    }
  }

  &__year {
    text-align: center;
    margin-top: auto;
    margin-bottom: auto;
    padding: 15px 0;
    line-height: 1;

    &--inactive {
      opacity: .6;
      pointer-events: none;
    }

    &:hover {
      cursor: pointer;
      font-weight: bold;
      color: #fff;
      background-color: #009FEA;
    }
  }

  &__month-selector {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 7px;
    font-size: 10px;
    font-family: 'Gotham';
    color: #787878;
    text-transform: uppercase;
    
    > div {
      padding: 16px 0;
      border-top: 1px solid rgba(0,0,0,.1);
      border-right: 1px solid rgba(0,0,0,.1);

      &:hover {
        cursor: pointer;
        font-weight: bold;
        color: #fff;
        background-color: #009FEA;
      }
    }
  }

  &__month {
    text-align: center;
    margin-top: auto;
    margin-bottom: auto;
    line-height: 1;

    &--inactive {
      opacity: .6;
      pointer-events: none;
    }

    &:hover {
      cursor: pointer;
      font-weight: bold;
    }

    .month__day:not(.month__day--othermonth):hover {
      color: #fff;
      background-color: #009FEA !important;
    }
    
    table {
      width: 100%;
    }
  }

  &__day-selector {
    padding: 12px 15px;
  }

  &__weekdays {
    width: 100%;
  }

  .week-days__day {
    font-family: 'Gotham';
    font-weight: bold;
    font-size: 11px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/DateInputDropdown.vue